import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FormControlComponent } from 'design/formControl';
import { EmailFieldset } from 'containers/EmailFieldset';
import { useEmailForm } from 'hooks/useEmailForm';
import { validateEmailFormValues } from 'utils/emailFormUtils';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { useServices } from 'hooks/useServices';
import { PartnerManagerData } from 'types/partnerManager';

type Props = {
  partnerId: number;
  parnerManagerData: PartnerManagerData;
  onCancel(): void;
  onEdited(): void;
};

export const PartnerManagerCredentialsEditor = (props: Props) => {
  const { partnerId, parnerManagerData, onCancel: cancel, onEdited } = props;

  const {
    partnersTableItem: { email },
  } = parnerManagerData;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { changePartnerEmail } = useServices();

  const { values, violations, setViolations, setEmailValue } = useEmailForm();

  const {
    partnerManager: { credentialsEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const submit = React.useCallback(() => {
    setViolations([]);

    const violations = validateEmailFormValues(values);

    if (violations.length > 0) {
      Promise.resolve(violations).then(setViolations);
    } else {
      const promise = changePartnerEmail(values, partnerId).then(() => {
        onEdited();
      });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    changePartnerEmail,
    injectPromise,
    onEdited,
    partnerId,
    setViolations,
    values,
  ]);

  React.useLayoutEffect(() => {
    setEmailValue(email);
  }, [email, setEmailValue]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={violations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <EmailFieldset
              disabled={asyncActive}
              values={values}
              violations={violations}
              onEmailValueChange={setEmailValue}
            />
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
